import {emotionStyled} from '@packages/utilities/styled';
import {tokens} from '../../themes';
import {Spinner} from '../Spinner';
import {Icon} from '../../icons/Icon';
import {
  disabledSelector as disabledSelectorBase,
  hoverSelector as hoverSelectorBase,
  activeSelector as activeSelectorBase,
} from '../../utils';

import {
  HSTACK_STYLES,
  STACK_VARIANTS,
  STACK_DEFAULT_VARIANTS,
} from '../Stack/Stack.styles';

// Selectors
const activeSelector = `&${activeSelectorBase}`;
const disabledSelector = disabledSelectorBase('&');
const hoverSelector = `&${hoverSelectorBase}`;
const isExpanded = '&:is([aria-expanded="true"])';

const sharedColorVariantStyles = {
  color: '$actionPrimaryText',
  boxShadow: '$actionColored',
};

export const ButtonView = emotionStyled('button')(
  {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    border: 'none',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    cursor: tokens.ui.activeCursor, // All interactive content in Designer uses `cursor: default`
    borderRadius: '$2',
    fontSize: '$body',
    lineHeight: '$body',
    fontWeight: '$normal',
    fontFamily: '$sansSerif',
    [disabledSelector]: {
      opacity: tokens.opacities.disabled,
      cursor: 'not-allowed',
    },
    '&:focus-visible': {
      outline: `1px solid ${tokens.colors.blueBorder}`,
      outlineOffset: '1px',
    },
  },
  {
    componentName: 'Button',
    variants: {
      colorVariant: {
        default: {
          color: '$actionSecondaryText',
          background: '$actionSecondaryBackground',
          boxShadow: '$actionSecondary',
          [`& ${Icon}`]: {
            color: '$textSecondary',
          },
          [`${hoverSelector}, ${isExpanded}`]: {
            background: '$actionSecondaryBackgroundHover',
            color: '$actionSecondaryTextHover',
            [`& ${Icon}`]: {
              color: '$actionSecondaryTextHover',
            },
          },
        },
        primary: {
          ...sharedColorVariantStyles,
          background: '$actionPrimaryBackground',
          [`${hoverSelector}, ${isExpanded}`]: {
            background: '$actionPrimaryBackgroundHover',
            color: '$actionPrimaryTextHover',
          },
        },
        danger: {
          ...sharedColorVariantStyles,
          background: '$redBackground',
          [`${hoverSelector}, ${isExpanded}`]: {
            background: '$redBackgroundHover',
            color: '$actionPrimaryTextHover',
          },
        },
        success: {
          ...sharedColorVariantStyles,
          background: '$greenBackground',
          [`${hoverSelector}, ${isExpanded}`]: {
            background: '$greenBackgroundHover',
            color: '$actionPrimaryTextHover',
          },
        },
        orange: {
          ...sharedColorVariantStyles,
          background: '$orangeBackground',
          [`${hoverSelector}, ${isExpanded}`]: {
            background: '$orangeBackgroundHover',
            color: '$actionPrimaryTextHover',
          },
        },
        purple: {
          ...sharedColorVariantStyles,
          background: '$purpleBackground',
          [`${hoverSelector}, ${isExpanded}`]: {
            background: '$purpleBackgroundHover',
            color: '$actionPrimaryTextHover',
          },
        },
        blue: {
          ...sharedColorVariantStyles,
          background: '$blueBackground',
          [`${hoverSelector}, ${isExpanded}`]: {
            background: '$blueBackgroundHover',
            color: '$actionPrimaryTextHover',
          },
        },
        green: {
          ...sharedColorVariantStyles,
          background: '$greenBackground',
          [`${hoverSelector}, ${isExpanded}`]: {
            background: '$greenBackgroundHover',
            color: '$actionPrimaryTextHover',
          },
        },
        white: {
          color: '$textBlackPrimary',
          background: '$white',
          [`${hoverSelector}, ${isExpanded}`]: {
            background: '$actionWhiteSolidBackgroundHover',
            color: '$textBlackPrimary',
          },
          [`& ${Spinner}`]: {
            color: '$textBlackPrimary',
          },
          '&:focus-visible': {
            outline: '$white',
          },
        },
        // The following colorVariants don't have `solid` variant implementations
        secondary: {},
        black: {},
        pink: {
          ...sharedColorVariantStyles,
          background: '$pinkBackground',
          [`${hoverSelector}, ${isExpanded}`]: {
            background: '$pinkBackgroundHover',
            color: '$actionPrimaryTextHover',
          },
        },
        red: {},
      },
      density: {
        comfortable: {
          padding: tokens.ui.button.padding.comfortable,
        },
        compact: {
          padding: tokens.ui.button.padding.compact,
        },
      },
      variant: {
        solid: {},
        subtle: {
          color: '$actionSecondaryText',
          background: 'transparent',
          boxShadow: 'none',
          [hoverSelector]: {
            background: '$uiBackgroundHover',
            color: '$actionSecondaryTextHover',
          },
          [`${isExpanded}, ${isExpanded}:hover`]: {
            background: '$uiBackgroundActive',
          },
        },
        ghost: {
          color: '$actionSecondaryText',
          background: 'transparent',
          boxShadow: 'none',
          [hoverSelector]: {
            background: '$uiBackgroundHover',
            color: '$actionSecondaryTextHover',
          },
          [`${activeSelector}, ${isExpanded}, ${isExpanded}:hover`]: {
            background: '$uiBackgroundActive',
          },
        },
        outline: {
          color: '$actionSecondaryText',
          background: 'transparent',
          boxShadow: `0 0 0 1px ${tokens.colors.actionSecondaryBorder} inset`,
          [hoverSelector]: {
            background: 'transparent',
            color: '$actionSecondaryTextHover',
            boxShadow: `0 0 0 1px ${tokens.colors.actionSecondaryBorderHover} inset`,
          },
          '&:focus-visible:not(:active)': {
            boxShadow: 'none',
          },
          [`${isExpanded}, ${isExpanded}:hover`]: {
            background: '$uiBackgroundActive',
            boxShadow: 'none',
          },
        },
      },
      isFullWidth: {
        true: {
          display: 'flex',
          width: '100%',
        },
      },
      isLoading: {
        true: {},
      },
      isSelected: {
        true: {},
      },
      shouldTruncate: {
        true: {
          /**
           * We need to set `min-width: 0` here to allow children to truncate in a flex context.
           * However, setting `min-width: 0` everywhere has negative effects in many other contexts.
           *
           * @see https://css-tricks.com/flexbox-truncated-text/#aa-the-solution-is-min-width-0-on-the-flex-child
           */
          minWidth: 0,
          /**
           * This is necessary to constrain the truncated Button to the width of its parent wrapper.
           */
          width: '100%',
        },
      },
      isBranded: {
        true: {
          fontFamily: '$brandedBody',
          fontSize: '15px',
          fontWeight: '$medium',
          lineHeight: '$brandedBody',
          padding: '$3 $4',
        },
      },
    },
    compoundVariants: [
      /**
       * Solid & isLoading
       */
      {
        conditions: {
          colorVariant: [
            'primary',
            'danger',
            'success',
            'orange',
            'purple',
            'pink',
          ],
          variant: 'solid',
          isLoading: true,
        },
        css: {
          [`& ${Spinner}`]: {
            color: '$actionPrimaryText',
          },
        },
      },
      /**
       * isSelected & Default Solid Variant
       */
      {
        conditions: {
          colorVariant: ['default'],
          isSelected: true,
          variant: 'solid',
        },
        css: {
          background: '$actionPrimaryBackground',
          color: '$actionPrimaryText',
          [`& ${Icon}`]: {
            color: '$actionPrimaryText',
          },
        },
      },
      /**
       * isSelected & Outline/Ghost Variant
       */
      {
        conditions: {
          colorVariant: [
            'default',
            'secondary',
            'blue',
            'green',
            'purple',
            'orange',
            'red',
            'white',
            'pink',
          ],
          isSelected: true,
          variant: ['outline', 'ghost'],
        },
        css: {
          '&&': {
            boxShadow: 'none',
            background: '$uiBackgroundActive',
            [`& ${Icon}`]: {
              color: '$textPrimary',
            },
            [`${hoverSelector}, ${isExpanded}`]: {
              boxShadow: 'none',
              background: '$uiBackgroundActive',
            },
          },
        },
      },
      /** isSelected & White Outline/Ghost Variant */
      {
        conditions: {
          colorVariant: 'white',
          isSelected: true,
          variant: ['outline', 'ghost'],
        },
        css: {
          '&&': {
            boxShadow: 'none',
            background: '$actionSecondaryBackgroundHover',
            color: '$textWhitePrimary',
            [`& ${Icon}`]: {
              color: '$textWhitePrimary',
            },
            [`${hoverSelector}, ${isExpanded}`]: {
              boxShadow: 'none',
              background: '$actionSecondaryBackgroundHover',
              color: '$textWhitePrimary',
            },
          },
        },
      },
      /**
       * isSelected & Black Outline Variant
       */
      {
        conditions: {
          colorVariant: ['black'],
          isSelected: true,
          variant: ['outline', 'ghost'],
        },
        css: {
          '&&': {
            boxShadow: 'none',
            background: tokens.colors.blackA10,
          },
        },
      },
      /**
       * isSelected & Ghost Variant
       */
      {
        conditions: {
          colorVariant: ['blue'],
          isSelected: true,
          variant: ['ghost'],
        },
        css: {
          '&&': {
            background: '$uiBackgroundActive',
            color: '$blueText',
            [`& ${Icon}`]: {
              color: '$blueText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: ['green'],
          isSelected: true,
          variant: ['ghost'],
        },
        css: {
          '&&': {
            background: '$uiBackgroundActive',
            color: '$greenText',
            [`& ${Icon}`]: {
              color: '$greenText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: ['orange'],
          isSelected: true,
          variant: ['ghost'],
        },
        css: {
          '&&': {
            background: '$uiBackgroundActive',
            color: '$orangeText',
            [`& ${Icon}`]: {
              color: '$orangeText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: ['purple'],
          isSelected: true,
          variant: ['ghost'],
        },
        css: {
          '&&': {
            background: '$uiBackgroundActive',
            color: '$purpleText',
            [`& ${Icon}`]: {
              color: '$purpleText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: ['red'],
          isSelected: true,
          variant: ['ghost'],
        },
        css: {
          '&&': {
            background: '$uiBackgroundActive',
            color: '$redText',
            [`& ${Icon}`]: {
              color: '$redText',
            },
          },
        },
      },
      /**
       * Outline Variant
       */
      {
        conditions: {
          colorVariant: ['secondary'],
          variant: ['outline'],
        },
        css: {
          '&&': {
            color: '$textSecondary',
            [hoverSelector]: {
              color: '$textSecondary',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: ['black'],
          variant: 'outline',
        },
        css: {
          '&&': {
            boxShadow: `0 0 0 1px ${tokens.colors.uiBlackGhostBorder} inset`,
            color: '$black',
            [hoverSelector]: {
              color: '$black',
              boxShadow: `0 0 0 1px ${tokens.colors.uiBlackGhostBorderHover} inset`,
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: ['blue'],
          variant: 'outline',
        },
        css: {
          '&&': {
            color: '$blueText',
            [hoverSelector]: {
              color: '$blueText',
            },
            [`& ${Icon}`]: {
              color: '$blueText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: ['green'],
          variant: 'outline',
        },
        css: {
          '&&': {
            color: '$greenText',
            [hoverSelector]: {
              color: '$greenText',
            },
            [`& ${Icon}`]: {
              color: '$greenText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: ['orange'],
          variant: 'outline',
        },
        css: {
          '&&': {
            color: '$orangeText',
            [hoverSelector]: {
              color: '$orangeText',
            },
            [`& ${Icon}`]: {
              color: '$orangeText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: ['purple'],
          variant: 'outline',
        },
        css: {
          '&&': {
            color: '$purpleText',
            [hoverSelector]: {
              color: '$purpleText',
            },
            [`& ${Icon}`]: {
              color: '$purpleText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: ['red'],
          variant: 'outline',
        },
        css: {
          '&&': {
            color: '$redText',
            [hoverSelector]: {
              color: '$redText',
            },
            [`& ${Icon}`]: {
              color: '$redText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: ['pink'],
          variant: 'outline',
        },
        css: {
          '&&': {
            color: '$pinkText',
            [hoverSelector]: {
              color: '$pinkText',
            },
            [`& ${Icon}`]: {
              color: '$pinkText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: ['white'],
          variant: 'outline',
        },
        css: {
          '&&': {
            boxShadow: `0 0 0 1px ${tokens.colors.actionWhiteOutlineBorder} inset`,
            color: '$white',
            [hoverSelector]: {
              color: '$white',
              boxShadow: `0 0 0 1px ${tokens.colors.actionWhiteOutlineBorderHover} inset`,
            },
            [`${activeSelector}, ${isExpanded}, ${isExpanded}:hover`]: {
              background: '$actionWhiteOutlineBackgroundSelected',
            },
            [`& ${Spinner}`]: {
              color: '$textWhitePrimary',
            },
          },
        },
      },
      /**
       * Ghost Variant
       */
      {
        conditions: {
          colorVariant: 'secondary',
          variant: 'ghost',
        },
        css: {
          color: '$textSecondary',
          [`${hoverSelector}, ${isExpanded}`]: {
            color: '$textSecondary',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'blue',
          variant: 'ghost',
        },
        css: {
          color: '$blueText',
          [`${hoverSelector}, ${isExpanded}`]: {
            color: '$blueText',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'blue',
          variant: 'ghost',
        },
        css: {
          color: '$blueText',
          [`${hoverSelector}, ${isExpanded}`]: {
            color: '$blueText',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'green',
          variant: 'ghost',
        },
        css: {
          color: '$greenText',
          [`${hoverSelector}, ${isExpanded}`]: {
            color: '$greenText',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'orange',
          variant: 'ghost',
        },
        css: {
          color: '$orangeText',
          [`${hoverSelector}, ${isExpanded}`]: {
            color: '$orangeText',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'purple',
          variant: 'ghost',
        },
        css: {
          color: '$purpleText',
          [`${hoverSelector}, ${isExpanded}`]: {
            color: '$purpleText',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'pink',
          variant: 'ghost',
        },
        css: {
          color: '$pinkText',
          [`${hoverSelector}, ${isExpanded}`]: {
            color: '$pinkText',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'red',
          variant: 'ghost',
        },
        css: {
          color: '$redText',
          [`${hoverSelector}, ${isExpanded}`]: {
            color: '$redText',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'white',
          variant: 'ghost',
        },
        css: {
          color: '$textWhitePrimary',
          [`${hoverSelector}, ${isExpanded}`]: {
            color: '$textWhitePrimary',
            background: '$actionWhiteGhostBackgroundHover',
          },
          [`${activeSelector}, ${isExpanded}, ${isExpanded}:hover`]: {
            background: '$actionWhiteGhostBackgroundSelected',
          },
          [`& ${Spinner}`]: {
            color: '$textWhitePrimary',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'black',
          variant: 'ghost',
        },
        css: {
          color: '$textBlackPrimary',
          [`${hoverSelector}, ${isExpanded}`]: {
            background: '$actionBlackGhostBackgroundHover',
            color: '$textBlackPrimary',
          },
          [`${activeSelector}, ${isExpanded}, ${isExpanded}:hover`]: {
            background: '$actionBlackGhostBackgroundSelected',
          },
          [`& ${Spinner}`]: {
            color: '$textBlackPrimary',
          },
        },
      },
      /**
       * Subtle Variant
       */
      {
        conditions: {
          colorVariant: 'default',
          variant: 'subtle',
        },
        css: {
          '&&': {
            color: '$textPrimary',
            background: '$backgroundRaised',
            boxShadow: 'none',
            [`${hoverSelector}, ${isExpanded}`]: {
              background: '$actionSecondaryBackground',
              color: '$textPrimary',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: 'blue',
          variant: 'subtle',
        },
        css: {
          '&&': {
            color: '$blueText',
            background: '$blueTransparent',
            boxShadow: 'none',
            [`${hoverSelector}, ${isExpanded}`]: {
              background: '$blueTransparentHover',
              color: '$blueText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: 'green',
          variant: 'subtle',
        },
        css: {
          '&&': {
            color: '$greenText',
            background: '$greenTransparent',
            boxShadow: 'none',
            [`${hoverSelector}, ${isExpanded}`]: {
              background: '$greenTransparentHover',
              color: '$greenText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: 'orange',
          variant: 'subtle',
        },
        css: {
          '&&': {
            color: '$orangeText',
            background: '$orangeTransparent',
            boxShadow: 'none',
            [`${hoverSelector}, ${isExpanded}`]: {
              background: '$orangeTransparentHover',
              color: '$orangeText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: 'pink',
          variant: 'subtle',
        },
        css: {
          '&&': {
            color: '$pinkText',
            background: '$pinkTransparent',
            boxShadow: 'none',
            [`${hoverSelector}, ${isExpanded}`]: {
              background: '$pinkTransparentHover',
              color: '$pinkText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: 'purple',
          variant: 'subtle',
        },
        css: {
          '&&': {
            color: '$purpleText',
            background: '$purpleTransparent',
            boxShadow: 'none',
            [`${hoverSelector}, ${isExpanded}`]: {
              background: '$purpleTransparentHover',
              color: '$purpleText',
            },
          },
        },
      },
      {
        conditions: {
          colorVariant: 'red',
          variant: 'subtle',
        },
        css: {
          '&&': {
            color: '$redText',
            background: '$redTransparent',
            boxShadow: 'none',
            [`${hoverSelector}, ${isExpanded}`]: {
              background: '$redTransparentHover',
              color: '$redText',
            },
          },
        },
      },
      /**
       * isSelected & Subtle Variant
       */
      {
        conditions: {
          colorVariant: 'default',
          isSelected: true,
          variant: 'subtle',
        },
        css: {
          '&&': {
            background: '$actionSecondaryBackground',
            color: '$textPrimary',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'blue',
          isSelected: true,
          variant: 'subtle',
        },
        css: {
          '&&': {
            background: '$blueTransparentHover',
            color: '$blueText',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'green',
          isSelected: true,
          variant: 'subtle',
        },
        css: {
          '&&': {
            background: '$greenTransparentHover',
            color: '$greenText',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'orange',
          isSelected: true,
          variant: 'subtle',
        },
        css: {
          '&&': {
            background: '$orangeTransparentHover',
            color: '$orangeText',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'pink',
          isSelected: true,
          variant: 'subtle',
        },
        css: {
          '&&': {
            background: '$pinkTransparentHover',
            color: '$pinkText',
          },
        },
      },
      {
        conditions: {
          colorVariant: 'purple',
          isSelected: true,
          variant: 'subtle',
        },
        css: {
          '&&': {
            background: '$purpleTransparentHover',
            color: '$purpleText',
          },
        },
      },
      /**
       * isBranded & density compact
       */
      {
        conditions: {
          isBranded: true,
          density: 'compact',
        },
        css: {
          padding: '$2 $4',
          fontSize: '$brandedBody',
        },
      },
    ],
    defaultVariants: {
      colorVariant: 'default',
      density: 'comfortable',
      variant: 'solid',
      isBranded: false,
    },
  }
);

export const styleTokens = {
  ButtonContentViewWidth: '--component-button-content-view-width',
};

export const ButtonContentView = emotionStyled('span')(
  {
    ...HSTACK_STYLES,
    textAlign: 'center',
    width: `var(${styleTokens.ButtonContentViewWidth}, initial)`,
  },
  {
    componentName: 'ButtonContentView',
    variants: {
      justify: STACK_VARIANTS.justify,
      shouldEdgeAlignIcons: {
        true: {
          flex: '1 1 auto',
        },
      },
      shouldTruncate: {
        true: {
          /**
           * Forces the HStack to hide overflowing text.
           */
          overflow: 'hidden',
        },
      },
    },
    defaultVariants: {
      justify: STACK_DEFAULT_VARIANTS.justify,
    },
  }
);

export const SpaceSaver = emotionStyled('span')(
  {
    ...HSTACK_STYLES,
    gap: tokens.space[1],
    width: '100%',
  },
  {
    componentName: 'SpaceSaver',
    variants: {
      hide: {
        true: {
          visibility: 'hidden',
        },
      },
      justify: STACK_VARIANTS.justify,
    },
    defaultVariants: {
      justify: STACK_DEFAULT_VARIANTS.justify,
    },
  }
);

export const LoadingWrapper = emotionStyled('span')({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
